/**
 * Enhanced HomeworkEdit using the new component system
 * Demonstrates form layout with React Quill editing, Pinyin generation, and dynamic questions
 */

import isEmpty from 'lodash/isEmpty';
import { parse } from 'node-html-parser';
import React, { useEffect, useState } from 'react';
import { FiBook, FiUpload, FiEye, FiAlertCircle } from 'react-icons/fi';
import { RiBold, RiPinyinInput } from 'react-icons/ri';
import 'react-quill/dist/quill.snow.css';
import { useNavigate, useParams } from 'react-router-dom';

// New component system imports
import {
  Box,
  Button,
  HStack,
  Icon,
  Select,
  useToast,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  Text,
  Tooltip,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import {
  Card,
  FormField,
  FormPageLayout,
  FormSection,
  Input,
} from '@grcs/components';

// Rich text editors (lazy loaded for better performance)
import { ArticleEditor } from '../shared/components/editors/ArticleEditor';
import { QuestionEditor } from '../shared/components/editors/QuestionEditor';

// Existing hooks and utilities
import { useUserOperations } from '@grcs/hook/useDatabase';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import { ArticleProfile } from '@grcs/types/DataTypes';
import { countArticleCharacters } from '@grcs/utils/content/countArticleCharacters';
import { TABLE_NAMES } from '@grcs/utils/database/types';
import { uploadFile } from '@grcs/utils/storage/databaseStorageLoader';
import { parseAndBold } from '@grcs/utils/formatting/parseAndBold';
import { parseAndDisplayPinyin } from '@grcs/utils/formatting/parseAndDisplayPinyin';
import { ALL_CHAPTER, ALL_GRADES, FILE_DB_BUCKET_NAME } from '../const';

interface HomeworkFormData {
  title: string;
  grade: string;
  chapter: string;
  article: string;
  article_pinyin: string;
  question: string;
  question_pinyin: string;
  file: File | null;
}

export const HomeworkEditEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const userOps = useUserOperations();
  const toast = useToast();
  const { contentId } = useParams();
  const [{ full_name }] = useLocalStorage('user', {});

  // Color mode values - must be at top level
  const bgColor = useColorModeValue('blue.50', 'blue.900');
  const borderColor = useColorModeValue('blue.200', 'blue.600');
  const actionBoxBg = useColorModeValue('gray.50', 'gray.700');
  const accordionBgBlue = useColorModeValue('blue.50', 'blue.900');
  const accordionBgGreen = useColorModeValue('green.50', 'green.900');
  const accordionBgPurple = useColorModeValue('purple.50', 'purple.900');

  const [content, setContent] = useState<ArticleProfile | null>(null);
  const [formData, setFormData] = useState<HomeworkFormData>({
    title: '',
    grade: '',
    chapter: '',
    article: '',
    article_pinyin: '',
    question: '[]', // Initialize as empty JSON array for questions
    question_pinyin: '[]',
    file: null,
  });

  const [errors, setErrors] = useState<Partial<HomeworkFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Load existing content with optimized query
  useEffect(() => {
    const fetchContent = async () => {
      if (!contentId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Use direct supabase query for better performance
        const { data: contentData, error } = await userOps.supabaseClient
          .from(TABLE_NAMES.LIBRARY)
          .select(
            'id, title, grade, chapter, article, article_pinyin, question, question_pinyin, file_name, author, created_at, updated_at',
          )
          .eq('id', contentId)
          .eq('deleted', false)
          .limit(1)
          .single(); // Use single() instead of getting an array

        if (error) {
          if (error.code === 'PGRST116') {
            // No rows found
            toast({
              title: 'Content Not Found',
              description: 'The requested homework content could not be found.',
              status: 'error',
              duration: 5000,
              isClosable: true,
            });
          } else {
            throw error;
          }
          navigate('/admin/library');
          return;
        }

        if (contentData) {
          const article = contentData as ArticleProfile;
          setContent(article);
          // Prepopulate form with existing data
          setFormData({
            title: article.title || '',
            grade: article.grade || '',
            chapter: article.chapter || '',
            article: article.article || '',
            article_pinyin: article.article_pinyin || '',
            question: article.question || '[]',
            question_pinyin: article.question_pinyin || '[]',
            file: null,
          });
        }
      } catch (error: any) {
        console.error('Error loading content:', error);
        toast({
          title: 'Error Loading Content',
          description: error.message || 'Failed to load homework content',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        navigate('/admin/library');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [contentId, userOps.supabaseClient, toast, navigate]);

  // Pinyin and formatting functions
  const generatePinyin = (text: string) => {
    const tmp = parse(text || '');
    const tmp_pinyin = parseAndDisplayPinyin({
      article: tmp.textContent,
      grade: formData.grade,
      chapter: formData.chapter,
    });
    return tmp_pinyin;
  };

  const handleGeneratePinyin = () => {
    if (!validateInputsForPinyin()) return;
    setFormData((prev) => ({
      ...prev,
      article_pinyin: generatePinyin(prev.article),
    }));
  };

  const handleGeneratePinyinForQuestion = () => {
    if (!validateInputsForPinyin()) return;
    setFormData((prev) => ({
      ...prev,
      question_pinyin: generatePinyin(prev.question),
    }));
  };

  const handleBoldWords = () => {
    if (!validateInputsForPinyin()) return;

    const tmp_article = parse(formData.article || '');
    const articleWithBold = parseAndBold({
      article: tmp_article.textContent,
      grade: formData.grade,
      chapter: formData.chapter,
    });

    const pinyinArticle = parse(formData.article_pinyin || '');
    const pinyinArticleWithBold = parseAndBold({
      article: pinyinArticle.textContent,
      grade: formData.grade,
      chapter: formData.chapter,
    });

    setFormData((prev) => ({
      ...prev,
      article: articleWithBold,
      article_pinyin: pinyinArticleWithBold,
    }));
  };

  const validateInputsForPinyin = (): boolean => {
    if (!formData.article) {
      toast({
        title: 'Error generating Pinyin',
        description: 'Please enter an article first.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    if (!formData.grade) {
      toast({
        title: 'Error generating Pinyin',
        description: 'Please select a grade first.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    if (!formData.chapter) {
      toast({
        title: 'Error generating Pinyin',
        description: 'Please select a chapter first.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return false;
    }
    return true;
  };

  const handleInputChange = (
    field: keyof HomeworkFormData,
    value: string | File,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleInputChange('file', file);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<HomeworkFormData> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.grade) {
      newErrors.grade = 'Grade is required';
    }

    if (!formData.chapter) {
      newErrors.chapter = 'Chapter is required';
    }

    if (!formData.article.trim()) {
      newErrors.article = 'Article content is required';
    }

    if (!formData.question.trim()) {
      newErrors.question = 'Questions are required';
    }

    if (!formData.article_pinyin.trim()) {
      newErrors.article_pinyin =
        'Article Pinyin is required. Please generate it using the button.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(10);

    try {
      // Handle file upload if present
      if (formData.file && !isEmpty(formData.file)) {
        setUploadProgress(30);
        const { error: uploadError } = await uploadFile({
          file: formData.file,
          bucket: FILE_DB_BUCKET_NAME,
          supabase: userOps.supabaseClient,
        });

        if (uploadError) {
          toast({
            title: 'File Upload Failed',
            description: uploadError.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
      }

      setUploadProgress(70);

      // Update content in database
      const updateData: Partial<ArticleProfile> = {
        title: formData.title,
        grade: formData.grade,
        chapter: formData.chapter,
        article: formData.article,
        article_pinyin: formData.article_pinyin,
        question: formData.question,
        question_pinyin: formData.question_pinyin,
        author: full_name,
        updated_at: new Date().toISOString(),
        file_name: formData.file?.name || content?.file_name || '',
        word_count: countArticleCharacters(formData.article),
      };

      const { error: contentError } = await userOps.updateDataById(
        TABLE_NAMES.LIBRARY,
        'id',
        contentId!,
        updateData,
      );

      if (contentError) {
        toast({
          title: 'Content Update Failed',
          description: contentError.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      setUploadProgress(100);

      toast({
        title: 'Content Updated Successfully',
        description: 'Homework content has been updated successfully.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      // Navigate back to library
      navigate('/admin/library');
    } catch (error) {
      console.error('Error updating homework:', error);
      toast({
        title: 'Unexpected Error',
        description:
          'An unexpected error occurred while updating the homework.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = () => {
    navigate('/admin/library');
  };

  if (isLoading) {
    return (
      <FormPageLayout
        title="Loading..."
        subtitle="Loading homework content for editing"
        breadcrumb={{
          items: [
            { label: 'Admin', href: '/admin' },
            { label: 'Library', href: '/admin/library' },
            { label: 'Edit', isCurrent: true },
          ],
        }}
        loading={true}
        maxWidth="1200px"
      >
        <VStack spacing={4} align="center" py={10}>
          <div>Loading homework content...</div>
          <div style={{ fontSize: '0.875rem', opacity: 0.7 }}>
            This may take a moment
          </div>
        </VStack>
      </FormPageLayout>
    );
  }

  // Add content check after loading
  if (!content) {
    return (
      <FormPageLayout
        title="Content Not Found"
        subtitle="The requested homework content could not be found"
        breadcrumb={{
          items: [
            { label: 'Admin', href: '/admin' },
            { label: 'Library', href: '/admin/library' },
            { label: 'Edit', isCurrent: true },
          ],
        }}
        maxWidth="1200px"
      >
        <VStack spacing={4} align="center" py={10}>
          <div>Content not found</div>
          <Button onClick={() => navigate('/admin/library')}>
            Back to Library
          </Button>
        </VStack>
      </FormPageLayout>
    );
  }

  const handlePreview = () => {
    navigate(`/admin/library/${contentId}/view`);
  };

  return (
    <FormPageLayout
      title={`Edit: ${content?.title || 'Homework'}`}
      subtitle="Edit reading comprehension content in the library"
      breadcrumb={{
        items: [
          { label: 'Admin', href: '/admin' },
          { label: 'Library', href: '/admin/library' },
          { label: 'Edit', isCurrent: true },
        ],
      }}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      submitText="Save Changes"
      maxWidth="1200px"
      loading={isSubmitting}
      submitDisabled={isSubmitting || !content}
      formActions={
        <Button
          leftIcon={<FiEye />}
          variant="outline"
          colorScheme="blue"
          onClick={handlePreview}
        >
          Preview
        </Button>
      }
    >
      <VStack spacing={6} align="stretch" width="100%">
        {/* Quick Guide */}
        <Alert
          status="info"
          borderRadius="lg"
          bg={bgColor}
          border="1px solid"
          borderColor={borderColor}
        >
          <AlertIcon as={FiAlertCircle} />
          <Box flex="1">
            <AlertTitle fontSize="md" mb={2}>
              📝 Editing Workflow
            </AlertTitle>
            <AlertDescription fontSize="sm">
              <VStack align="start" spacing={1}>
                <Text>
                  <Badge colorScheme="blue" mr={2}>
                    1
                  </Badge>
                  Edit the title, grade, and chapter information
                </Text>
                <Text>
                  <Badge colorScheme="blue" mr={2}>
                    2
                  </Badge>
                  Write or paste your article content
                </Text>
                <Text>
                  <Badge colorScheme="blue" mr={2}>
                    3
                  </Badge>
                  Click "Generate Pinyin" to add pronunciation guides
                </Text>
                <Text>
                  <Badge colorScheme="blue" mr={2}>
                    4
                  </Badge>
                  Use "Bold Chapter New Words" to highlight vocabulary
                </Text>
                <Text>
                  <Badge colorScheme="blue" mr={2}>
                    5
                  </Badge>
                  Add comprehension questions with multiple choice answers
                </Text>
                <Text>
                  <Badge colorScheme="blue" mr={2}>
                    6
                  </Badge>
                  Click "Save Changes" to update the homework
                </Text>
              </VStack>
            </AlertDescription>
          </Box>
        </Alert>

        {/* Accordion Sections for Better Organization */}
        <Accordion defaultIndex={[0, 1, 2]} allowMultiple>
          {/* Section 1: Basic Information */}
          <AccordionItem
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            mb={4}
          >
            <h2>
              <AccordionButton
                py={4}
                _expanded={{ bg: accordionBgBlue, fontWeight: 'bold' }}
              >
                <Box flex="1" textAlign="left">
                  <HStack spacing={3}>
                    <Badge colorScheme="blue" fontSize="md">
                      1
                    </Badge>
                    <Text fontSize="lg" fontWeight="semibold">
                      📋 Information
                    </Text>
                  </HStack>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={6} pt={6}>
              <VStack spacing={6} align="stretch">
                <FormField
                  label="Title"
                  required
                  error={errors.title}
                  // helperText="A descriptive title for the homework"
                >
                  <Input
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Enter homework title"
                    leftIcon={<FiBook />}
                  />
                </FormField>

                <HStack spacing={6} align="flex-start" width="100%">
                  <FormField
                    label="Grade Level"
                    required
                    error={errors.grade}
                    flex={1}
                  >
                    <Select
                      value={formData.grade}
                      onChange={(e) =>
                        handleInputChange('grade', e.target.value)
                      }
                      placeholder="Select grade"
                    >
                      {ALL_GRADES.map((grade) => (
                        <option key={grade} value={grade}>
                          Grade {grade}
                        </option>
                      ))}
                    </Select>
                  </FormField>

                  <FormField
                    label="Chapter"
                    required
                    error={errors.chapter}
                    flex={1}
                  >
                    <Select
                      value={formData.chapter}
                      onChange={(e) =>
                        handleInputChange('chapter', e.target.value)
                      }
                      placeholder="Select chapter"
                    >
                      {ALL_CHAPTER.map((chapter) => (
                        <option key={chapter} value={chapter}>
                          Chapter {chapter}
                        </option>
                      ))}
                    </Select>
                  </FormField>
                </HStack>
              </VStack>
            </AccordionPanel>
          </AccordionItem>

          {/* Section 2: Article Content */}
          <AccordionItem
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            mb={4}
          >
            <h2>
              <AccordionButton
                py={4}
                _expanded={{ bg: accordionBgGreen, fontWeight: 'bold' }}
              >
                <Box flex="1" textAlign="left">
                  <HStack spacing={3}>
                    <Badge colorScheme="green" fontSize="md">
                      2
                    </Badge>
                    <Text fontSize="lg" fontWeight="semibold">
                      📝 Content
                    </Text>
                  </HStack>
                  {/* <Text fontSize="sm" color="gray.600" mt={1}>
                    The main reading passage and pronunciation guide
                  </Text> */}
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={6} pt={6}>
              <VStack spacing={6} align="stretch">
                <FormField
                  // label="Article Text"
                  required
                  error={errors.article}
                  // helperText="Use text editor to format the article"
                >
                  {content ? (
                    <ArticleEditor
                      value={formData.article}
                      onChange={(value) => handleInputChange('article', value)}
                    />
                  ) : (
                    <Box p={4} bg="gray.50" borderRadius="md">
                      Loading editor...
                    </Box>
                  )}
                </FormField>

                <Box p={4} bg={actionBoxBg} borderRadius="md">
                  {/* <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    mb={3}
                    color="gray.600"
                  >
                    📚 Quick Actions
                  </Text> */}
                  <HStack spacing={3} flexWrap="wrap">
                    <Tooltip
                      label="Automatically generate pinyin pronunciation for the article text based on grade and chapter level"
                      hasArrow
                    >
                      <Button
                        leftIcon={<RiPinyinInput />}
                        colorScheme="red"
                        onClick={handleGeneratePinyin}
                        size="sm"
                        variant="solid"
                      >
                        Generate Pinyin
                      </Button>
                    </Tooltip>
                    <Tooltip
                      label="Highlight new vocabulary words for this chapter in bold. Make sure grade and chapter are selected first!"
                      hasArrow
                    >
                      <Button
                        leftIcon={<RiBold />}
                        colorScheme="purple"
                        onClick={handleBoldWords}
                        size="sm"
                        variant="solid"
                      >
                        Bold New Words
                      </Button>
                    </Tooltip>
                    <Text fontSize="xs" color="gray.500" ml={2}>
                      <FiAlertCircle
                        style={{ display: 'inline', marginRight: '4px' }}
                      />
                      Select grade & chapter first
                    </Text>
                  </HStack>
                </Box>

                <FormField
                  label="Article Pinyin"
                  required
                  error={errors.article_pinyin}
                  helperText="Auto-generated pronunciation guide (click Generate Pinyin button above)"
                >
                  {content ? (
                    <ArticleEditor
                      value={formData.article_pinyin}
                      onChange={(value) =>
                        handleInputChange('article_pinyin', value)
                      }
                    />
                  ) : (
                    <Box p={4} bg="gray.50" borderRadius="md">
                      Loading editor...
                    </Box>
                  )}
                </FormField>
              </VStack>
            </AccordionPanel>
          </AccordionItem>

          {/* Section 3: Questions */}
          <AccordionItem
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            mb={4}
          >
            <h2>
              <AccordionButton
                py={4}
                _expanded={{ bg: accordionBgPurple, fontWeight: 'bold' }}
              >
                <Box flex="1" textAlign="left">
                  <HStack spacing={3}>
                    <Badge colorScheme="purple" fontSize="md">
                      3
                    </Badge>
                    <Text fontSize="lg" fontWeight="semibold">
                      ❓ Create Questions
                    </Text>
                  </HStack>
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Create multiple choice questions
                  </Text>
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel pb={6} pt={6}>
              <VStack spacing={6} align="stretch">
                <FormField
                  label="Questions"
                  required
                  error={errors.question}
                  helperText="Add questions with multiple choice answers. Use the + button to add new questions and choices."
                >
                  {content ? (
                    <QuestionEditor
                      value={formData.question}
                      onChange={(value) => handleInputChange('question', value)}
                    />
                  ) : (
                    <Box p={4} bg="gray.50" borderRadius="md">
                      Loading question editor...
                    </Box>
                  )}
                </FormField>

                <Box p={4} bg={actionBoxBg} borderRadius="md">
                  <Text
                    fontSize="sm"
                    fontWeight="semibold"
                    mb={3}
                    color="gray.600"
                  >
                    📝 Question Actions
                  </Text>
                  <Tooltip
                    label="Generate pinyin pronunciation for all questions and answers"
                    hasArrow
                  >
                    <Button
                      leftIcon={<RiPinyinInput />}
                      colorScheme="red"
                      onClick={handleGeneratePinyinForQuestion}
                      size="sm"
                      variant="solid"
                    >
                      Generate Pinyin for Questions
                    </Button>
                  </Tooltip>
                </Box>

                <FormField
                  label="Question Pinyin"
                  helperText="Auto-generated pronunciation guide for questions"
                >
                  {content ? (
                    <QuestionEditor
                      value={formData.question_pinyin}
                      onChange={(value) =>
                        handleInputChange('question_pinyin', value)
                      }
                    />
                  ) : (
                    <Box p={4} bg="gray.50" borderRadius="md">
                      Loading question editor...
                    </Box>
                  )}
                </FormField>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>

        {/* File Upload - Outside Accordion */}
        <FormSection
          title="Supporting Materials (Optional)"
          description="Optional files to accompany the homework"
        >
          <Card padding={6} variant="outline">
            <VStack spacing={4} align="stretch">
              <HStack>
                <Icon as={FiUpload} boxSize={5} />
                <div>
                  <div style={{ fontWeight: 600 }}>Upload Supporting File</div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                    Audio, images, or documents (optional)
                  </div>
                </div>
              </HStack>

              <input
                type="file"
                onChange={handleFileChange}
                style={{
                  padding: '8px',
                  border: '2px dashed #E2E8F0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
                accept=".pdf,.doc,.docx,.mp3,.wav,.jpg,.png,.gif"
              />

              {/* Show current file if exists and no new file selected */}
              {content?.file_name && !formData.file && (
                <Box
                  p={3}
                  bg="gray.50"
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderColor="gray.400"
                >
                  <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                    Current: {content.file_name}
                  </div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                    Select a new file to replace this one
                  </div>
                </Box>
              )}

              {formData.file && (
                <Box
                  p={3}
                  bg="blue.50"
                  borderRadius="md"
                  borderLeft="4px solid"
                  borderColor="blue.500"
                >
                  <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                    Selected: {formData.file.name}
                  </div>
                  <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>
                    Size: {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </Box>
              )}
            </VStack>
          </Card>
        </FormSection>

        {/* Upload Progress */}
        {isSubmitting && uploadProgress > 0 && (
          <Card padding={4} variant="filled">
            <VStack spacing={2}>
              <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                Updating homework content... {uploadProgress}%
              </div>
              <Box
                width="100%"
                height="8px"
                bg="gray.200"
                borderRadius="4px"
                overflow="hidden"
              >
                <Box
                  height="100%"
                  bg="blue.500"
                  borderRadius="4px"
                  width={`${uploadProgress}%`}
                  transition="width 0.3s ease"
                />
              </Box>
            </VStack>
          </Card>
        )}
      </VStack>
    </FormPageLayout>
  );
};
